import { LimitNames } from '@gonfalon/billing/types';
import { getLimitDisplayName, hasSubscriptionLimit } from '@gonfalon/billing/utils';

import { type Scopes } from '../../auth';
import { type Settings } from '../../settings';
import {
  BooleanTag,
  CircularIconButton,
  DataList,
  DataListItem,
  Flex,
  Icon,
  Module,
  ModuleContent,
  ModuleHeader,
  type RequestError,
  Time,
} from '../../shared';
import { Box } from '../../shared/components';
import {
  type Account,
  type AccountMetrics,
  hasSubscription,
  isHostBasedPlan,
  isPlanWithSSO,
  MetricType,
  PlanType,
  PlanTypeReadableNames,
  type Subscription,
  SubscriptionState,
  SubscriptionStateDescriptions,
  SubscriptionStateReadableNames,
} from '../model';

import { BillingLimitRow } from './BillingLimitRow';
import { DataExportTransfersLimitRow } from './DataExportTransfersLimitRow';
import { SubscriptionDateManager } from './SubscriptionDateManager';

import './SubscriptionAccountDetails.scss';

const renderVersion = (subscription: Subscription) => {
  const showVersion = subscription.planType !== PlanType.ENTERPRISE && hasSubscription(subscription);

  if (showVersion) {
    return `(version ${subscription?.version ? subscription.version : 0})`;
  }
  return '';
};

export const getBillingMethod = (subscription: Subscription): string | null => {
  // Only show billing method for subscriptions (not trials)
  if (!hasSubscription(subscription) && subscription.state !== SubscriptionState.PENDING_CANCELED_SUBSCRIPTION) {
    return null;
  }

  // Following gonfalon logic: invoiced accounts have no periodEndDate
  const isInvoiced = !subscription.periodEndDate;

  return isInvoiced ? 'Invoice' : 'Self-Serve';
};

type SubscriptionAccountDetailsProps = Readonly<{
  account: Account;
  settings: Settings;
  scopes: Scopes;
  subscription: Subscription;
  accountRequireMfa: boolean;
  onChangeTrialEnd: (date: Date) => void;
  onChangeGraceEnd: (date: Date) => void;
  subscriptionUsage?: AccountMetrics;
  subscriptionUsageLoading: boolean;
  subscriptionUsageError?: RequestError;
  onChangeEnterpriseCampaignDates: (date: Date) => void;
  onChangeGuardianCampaignDates: (date: Date) => void;
}>;

export const SubscriptionAccountDetails = ({
  account,
  settings,
  scopes,
  subscription,
  accountRequireMfa,
  onChangeGraceEnd,
  onChangeTrialEnd,
  subscriptionUsage,
  subscriptionUsageLoading,
  subscriptionUsageError,
  onChangeEnterpriseCampaignDates,
  onChangeGuardianCampaignDates,
}: SubscriptionAccountDetailsProps) => {
  const { enforceSeatLimits = false } = subscription;

  const today = new Date();
  const customerEnterpriseCampaign = subscription.campaigns.CustomerEnterpriseCampaign;
  const customerGuardianCampaign = subscription.campaigns.CustomerGuardianCampaign;

  const isEnterpriseCampaignActive =
    customerEnterpriseCampaign?.startDate &&
    customerEnterpriseCampaign?.endDate &&
    today >= new Date(customerEnterpriseCampaign.startDate) &&
    today <= new Date(customerEnterpriseCampaign.endDate);

  const isGuardianCampaignActive =
    customerGuardianCampaign?.startDate &&
    customerGuardianCampaign?.endDate &&
    today >= new Date(customerGuardianCampaign.startDate) &&
    today <= new Date(customerGuardianCampaign.endDate);

  const renderTrialStatusDescription = () => {
    if (isGuardianCampaignActive) {
      return 'Customer Guardian trial';
    }
    if (isEnterpriseCampaignActive) {
      return 'Customer Enterprise trial';
    }
    if (subscription.state === SubscriptionState.ACTIVE_TRIAL) {
      return `Prospect ${subscription.planType} trial`;
    }
    if (subscription.state === SubscriptionState.ACTIVE_TRIAL_WITH_SUBSCRIPTION) {
      return 'Prospect trial superseded by active subscription';
    }
    return 'Not on an active trial';
  };

  const showReadOnlyStartDate =
    subscription.readOnlyStartDate &&
    (subscription.state === SubscriptionState.USAGE_EXCEEDED_GRACE_PERIOD ||
      subscription.state === SubscriptionState.USAGE_EXCEEDED_READ_ONLY);

  // TODO: Remove if no longer provisioned on accounts
  const nonStandardLimits = [
    ...(!settings.removeServerConnections ? [[MetricType.SERVER_CONNECTIONS, 'Server Connections']] : []),
    [MetricType.AI_SERVICE_CONNECTIONS, 'AI Service Connections'],
    [MetricType.ANALYTICS_MAU, 'Analytics MAU'],
  ] as const;

  return (
    <Module>
      <ModuleHeader>Subscription</ModuleHeader>
      <ModuleContent snug>
        {subscription && (
          <DataList>
            <BillingLimitRow
              title="Seat count"
              limit={subscription._limits?.[MetricType.SEATS] ?? 0}
              usage={subscriptionUsage?.[MetricType.SEATS]}
              isLoading={subscriptionUsageLoading}
              error={subscriptionUsageError}
            />
            {!isHostBasedPlan(subscription.planType) && (
              <DataListItem>
                <Flex justify="space-between">
                  <Box>Enforce seat count</Box>
                  <Box ml="auto">
                    <BooleanTag
                      value={enforceSeatLimits}
                      tooltip="When enabled, account is prevented from creating new users above their seat count"
                    />
                  </Box>
                </Flex>
              </DataListItem>
            )}
            {(subscription._limitDetails ?? [])
              .filter(({ limit: limitName }) => limitName !== LimitNames.SEATS)
              .map(({ limit: limitName, value }) => {
                if (!hasSubscriptionLimit(value)) {
                  return null;
                }
                if (limitName === LimitNames.EVENTS_PUBLISHED && settings.vitalstatistixDataExportObservability) {
                  return (
                    <DataExportTransfersLimitRow
                      key={limitName}
                      account={account}
                      subscription={subscription}
                      limit={value}
                    />
                  );
                }
                return (
                  <BillingLimitRow
                    key={limitName}
                    title={getLimitDisplayName(limitName, 'titleName')}
                    limit={value}
                    usage={(subscriptionUsage as Record<string, number> | undefined)?.[limitName]}
                    isLoading={subscriptionUsageLoading}
                    error={subscriptionUsageError}
                  />
                );
              })}
            {nonStandardLimits.map(([metricKey, titleName]) => {
              const limit = subscription._limits[metricKey] ?? 0;
              if (!hasSubscriptionLimit(limit)) {
                return null;
              }
              return (
                <BillingLimitRow
                  key={metricKey}
                  title={titleName}
                  limit={limit}
                  usage={subscriptionUsage?.[metricKey]}
                  isLoading={subscriptionUsageLoading}
                  error={subscriptionUsageError}
                />
              );
            })}
            <DataListItem>
              <Flex justify="space-between">
                <Box>Subscription state</Box>
                <Box ml="auto">
                  {SubscriptionStateReadableNames[subscription.state]}{' '}
                  <CircularIconButton
                    iconName="info"
                    intent="neutral"
                    size="small"
                    tooltip={SubscriptionStateDescriptions[subscription.state]}
                    tooltipPlacement="top"
                    ariaLabel="Subscription state information"
                  />
                </Box>
              </Flex>
            </DataListItem>
            <DataListItem>
              <Flex justify="space-between">
                <Box>Plan name</Box>
                <Box ml="auto">
                  {subscription.planType !== null ? PlanTypeReadableNames[subscription.planType] : 'N/A'}{' '}
                  {renderVersion(subscription)}
                </Box>
              </Flex>
            </DataListItem>
            {subscription.billingInterval && (
              <DataListItem>
                <Flex justify="space-between" align="center">
                  <Box>Billing interval</Box>
                  <Box ml="auto">
                    <Flex align="center">
                      {subscription.billingInterval === 'MonthlyBilling' ? 'Monthly' : 'Annual'}
                    </Flex>
                  </Box>
                </Flex>
              </DataListItem>
            )}
            <DataListItem>
              <Flex justify="space-between">
                <Box>MFA Required</Box>
                <Box ml="auto">
                  <BooleanTag value={accountRequireMfa} />
                </Box>
              </Flex>
            </DataListItem>
            {isPlanWithSSO(subscription.planType) && (
              <DataListItem>
                <Flex justify="space-between">
                  <Box>SSO Add-on</Box>
                  <Box ml="auto">
                    <BooleanTag value={subscription._limits.SSO === 1} />
                  </Box>
                </Flex>
              </DataListItem>
            )}
            <DataListItem>
              <Flex justify="space-between">
                <Box>Trial status</Box>
                <Box ml="auto">{renderTrialStatusDescription()}</Box>
              </Flex>
            </DataListItem>
            {subscription.trialExtensionCount > 0 && (
              <DataListItem>
                <Flex justify="space-between">
                  <Box>Trial extension count</Box>
                  <Box ml="auto">{subscription.trialExtensionCount}</Box>
                </Flex>
              </DataListItem>
            )}
            {subscription.campaigns && customerGuardianCampaign && isGuardianCampaignActive && (
              <DataListItem>
                <Flex justify="space-between">
                  <Box>Customer Guardian trial period </Box>
                  <Box ml="auto">
                    <Flex align="center">
                      <Time datetime={subscription.campaigns.CustomerGuardianCampaign.startDate} format="L" />
                      —
                      <Time datetime={subscription.campaigns.CustomerGuardianCampaign.endDate} format="L" />
                      {settings.enableExtendingGuardianTrialCampaign && (
                        <SubscriptionDateManager
                          value={subscription.campaigns.CustomerGuardianCampaign.endDate}
                          onChange={onChangeGuardianCampaignDates}
                        />
                      )}
                    </Flex>
                  </Box>
                </Flex>
              </DataListItem>
            )}
            {subscription.campaigns &&
              customerEnterpriseCampaign &&
              isEnterpriseCampaignActive &&
              !isGuardianCampaignActive && (
                <DataListItem>
                  <Flex justify="space-between">
                    <Box>Customer Enterprise trial period </Box>
                    <Box ml="auto">
                      <Flex align="center">
                        <Time datetime={subscription.campaigns.CustomerEnterpriseCampaign.startDate} format="L" />
                        —
                        <Time datetime={subscription.campaigns.CustomerEnterpriseCampaign.endDate} format="L" />
                      </Flex>
                      {settings.enableExtendingEnterpriseTrialCampaign && (
                        <SubscriptionDateManager
                          value={subscription.campaigns.CustomerEnterpriseCampaign.endDate}
                          onChange={onChangeEnterpriseCampaignDates}
                        />
                      )}
                    </Box>
                  </Flex>
                </DataListItem>
              )}
            {subscription.state !== SubscriptionState.ACTIVE_SUBSCRIPTION && (
              <DataListItem>
                <Flex justify="space-between" align="center">
                  <Box>Prospect trial period</Box>
                  <Box ml="auto">
                    <Flex align="center">
                      <Time datetime={subscription.trialStartDate} format="L" />
                      —
                      <Time datetime={subscription.trialEndDate} format="L" />
                      {scopes.extendTrialGrace && (
                        <SubscriptionDateManager
                          value={subscription.trialEndDate}
                          onChange={onChangeTrialEnd}
                          maxFromToday
                        />
                      )}
                    </Flex>
                  </Box>
                </Flex>
              </DataListItem>
            )}
            <DataListItem>
              <Flex justify="space-between" align="center">
                <Box>Grace period</Box>
                <Box ml="auto">
                  <Flex align="center">
                    {subscription.graceStartDate ? <Time datetime={subscription.graceStartDate} format="L" /> : 'N/A'}—
                    {subscription.graceEndDate ? <Time datetime={subscription.graceEndDate} format="L" /> : 'N/A'}
                    {scopes.extendTrialGrace && (
                      <SubscriptionDateManager value={subscription.graceEndDate} onChange={onChangeGraceEnd} />
                    )}
                  </Flex>
                </Box>
              </Flex>
            </DataListItem>
            {showReadOnlyStartDate && (
              <DataListItem>
                <Flex justify="space-between" align="center">
                  <Box>Read-only start date</Box>
                  <Box ml="auto" data-testid="read-only-start-date">
                    <Time datetime={subscription.readOnlyStartDate} format="L" />
                  </Box>
                </Flex>
              </DataListItem>
            )}

            <DataListItem>
              <Flex justify="space-between" align="center">
                <Box>Billing Cycle</Box>
                <Box ml="auto">
                  <div className="billing-section">
                    {(() => {
                      const billingMethod = getBillingMethod(subscription);
                      return (
                        <>
                          {billingMethod && (
                            <div className="billing-section__method">
                              <Icon name={billingMethod === 'Invoice' ? 'invoice' : 'credit-card'} size="small" />
                              <span className="billing-section__method-text">{billingMethod}</span>
                            </div>
                          )}
                          <div className="billing-section__date-range">
                            <Icon name="calendar" size="small" />
                            <span className="billing-section__date-range-text">
                              {hasSubscription(subscription) && subscription.usagePeriodStartDate ? (
                                <Time datetime={subscription.usagePeriodStartDate} format="L" />
                              ) : (
                                'N/A'
                              )}
                              —
                              {hasSubscription(subscription) && subscription.usagePeriodEndDate ? (
                                <Time datetime={subscription.usagePeriodEndDate} format="L" />
                              ) : (
                                'N/A'
                              )}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </Box>
              </Flex>
            </DataListItem>
            {subscription.subscriptionType && subscription.subscriptionType !== 'Standard' && (
              <DataListItem>
                <Flex justify="space-between" align="center">
                  <Box>Subscription Type</Box>
                  <Box ml="auto">
                    <Flex align="center">{subscription.subscriptionType}</Flex>
                  </Box>
                </Flex>
              </DataListItem>
            )}
            {subscription.subscriptionType === 'Marketplace' && subscription.marketplaceProvider && (
              <DataListItem>
                <Flex justify="space-between" align="center">
                  <Box>Marketplace Provider</Box>
                  <Box ml="auto">
                    <Flex align="center">{subscription.marketplaceProvider}</Flex>
                  </Box>
                </Flex>
              </DataListItem>
            )}
          </DataList>
        )}
      </ModuleContent>
    </Module>
  );
};
